import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Calendar, User, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { format, parseISO } from 'date-fns';
import { fromZonedTime, formatInTimeZone } from 'date-fns-tz';
import { cn } from '@/lib/utils';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { getTodayInShopTz } from '@/utils/shopTime';
import { useCompletedTicketEfficiency } from '@/hooks/kpi/useCompletedTicketEfficiency';
import { fetchTicketLaborMinutes } from '@/lib/labor/ticketLabor';

export default function CompletedTickets() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();
  const [tickets, setTickets] = useState<any[]>([]);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // R12-M-2: Use shop TZ for default filter dates
  const [filters, setFilters] = useState(() => {
    const endDate = getTodayInShopTz(shopTimezone);
    const startDateObj = parseISO(endDate + 'T00:00:00');
    startDateObj.setDate(startDateObj.getDate() - 30);
    return {
      startDate: format(startDateObj, 'yyyy-MM-dd'),
      endDate,
      technicianId: 'all',
      searchCustomer: '',
    };
  });

  useEffect(() => {
    loadData();
  }, [activeShop, filters]);

  const loadData = async () => {
    if (!activeShop) return;

    try {
      setLoading(true);

      // Load technicians (only active for filtering)
      const { data: techData } = await supabase
        .from('technicians')
        .select('id, name')
        .eq('shop_id', activeShop.id)
        .eq('employment_status', 'Active')
        .order('name');

      setTechnicians(techData || []);

      // Load completed tickets
      let query = supabase
        .from('tickets')
        .select(`
          *,
          vehicle:vehicles(id, year, make, model, engine_size),
          ticket_jobs(
            job_id,
            actual_minutes,
            job_status,
            job:jobs(*)
          ),
          technician:technicians!tickets_completed_by_tech_id_fkey(name)
        `)
        .eq('shop_id', activeShop.id)
        .eq('status', 'Completed')
        // CSI-S1: exclude soft-deleted Completed tickets from the page list.
        .is('deleted_at', null)
        .gte('completed_at', fromZonedTime(`${filters.startDate}T00:00:00`, shopTimezone).toISOString())
        .lte('completed_at', fromZonedTime(`${filters.endDate}T23:59:59`, shopTimezone).toISOString())
        .order('completed_at', { ascending: false });

      if (filters.technicianId !== 'all') {
        query = query.eq('completed_by_tech_id', filters.technicianId);
      }

      if (filters.searchCustomer) {
        query = query.ilike('customer_name', `%${filters.searchCustomer}%`);
      }

      const { data, error } = await query;

      if (error) throw error;

      // oct20e LABOR (FD-2026-10-24-B, row 6a): actual labor = corrected session minutes, book = approved labor —
      // never tickets.predicted_minutes (the scheduling duration) nor a sum of ticket_jobs.actual_minutes (0 when an
      // episode is finalized without explicit allocations).
      const rows = data || [];
      const labor = await fetchTicketLaborMinutes(rows.map((t: any) => t.id));
      setTickets(rows.map((t: any) => ({ ...t, labor_actual_minutes: labor.get(t.id)?.actualLaborMinutes ?? null,
        labor_approved_minutes: labor.get(t.id)?.approvedLaborMinutes ?? null })));
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateEfficiency = (ticket: any) => {
    const bookMinutes = ticket.labor_approved_minutes ?? 0;
    const actualMinutes = ticket.labor_actual_minutes ?? 0;

    if (actualMinutes === 0) return null;
    return Math.round((bookMinutes / actualMinutes) * 100);
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Customer', 'Vehicle', 'Technician', 'Jobs', 'Book Time', 'Actual Time', 'Efficiency'];
    const rows = tickets.map(ticket => {
      const efficiency = calculateEfficiency(ticket);
      const bookMinutes = ticket.labor_approved_minutes ?? 0;
      const actualMinutes = ticket.labor_actual_minutes ?? 0;
      
      return [
        formatInTimeZone(new Date(ticket.completed_at), shopTimezone, 'yyyy-MM-dd HH:mm'),
        ticket.customer_name || 'N/A',
        `${ticket.vehicle?.year} ${ticket.vehicle?.make} ${ticket.vehicle?.model}`,
        ticket.technician?.name || 'N/A',
        ticket.ticket_jobs?.map((tj: any) => tj.job?.name).join('; ') || '',
        `${bookMinutes}m`,
        `${actualMinutes}m`,
        efficiency ? `${efficiency}%` : 'N/A'
      ];
    });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `completed-tickets-${filters.startDate}-to-${filters.endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: 'Export Complete',
      description: `Exported ${tickets.length} completed tickets`,
    });
  };

  // K-T-13-C: aggregate efficiency now sourced from canonical
  // `kpi_completed_ticket_efficiency` RPC. Replaces the prior
  // mean-of-per-ticket-ratios reduce with the canonical
  // SUM(predicted_minutes) / SUM(actual_minutes) ratio + sample gate.
  // The per-ticket `calculateEfficiency` helper above stays — it's
  // still used by table rows and the CSV export (per-row display, not
  // an aggregate). Filter set passed to the hook mirrors the panel's
  // tickets query exactly (window + tech + customer search).
  const { data: aggregateEfficiency } = useCompletedTicketEfficiency({
    shopId: activeShop?.id ?? '',
    startDate: filters.startDate,
    endDate: filters.endDate,
    shopTimezone,
    technicianId: filters.technicianId,
    customerSearch: filters.searchCustomer,
  });
  const avgEfficiencyPercent: number | null = aggregateEfficiency?.gated
    || aggregateEfficiency?.ratio == null
    ? null
    : Math.round(aggregateEfficiency.ratio * 100);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Completed Tickets</h1>
              <p className="text-muted-foreground">View and analyze completed work</p>
            </div>
          </div>
          <Button onClick={exportToCSV} disabled={tickets.length === 0}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Completed</CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{tickets.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Average Efficiency</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {avgEfficiencyPercent !== null ? `${avgEfficiencyPercent}%` : 'N/A'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Technicians</CardTitle>
              <User className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {new Set(tickets.map(t => t.completed_by_tech_id)).size}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
            <CardDescription>Filter completed tickets by date, technician, or customer</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                />
              </div>
              <div>
                <Label>Technician</Label>
                <Select value={filters.technicianId} onValueChange={(value) => setFilters(prev => ({ ...prev, technicianId: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Technicians</SelectItem>
                    {technicians.map(tech => (
                      <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Customer Name</Label>
                <Input
                  placeholder="Search customer..."
                  value={filters.searchCustomer}
                  onChange={(e) => setFilters(prev => ({ ...prev, searchCustomer: e.target.value }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tickets Table */}
        <Card>
          <CardHeader>
            <CardTitle>Completed Tickets</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8 text-muted-foreground">Loading...</div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">No completed tickets found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Technician</TableHead>
                    <TableHead>Jobs</TableHead>
                    <TableHead className="text-right">Book Time</TableHead>
                    <TableHead className="text-right">Actual Time</TableHead>
                    <TableHead className="text-right">Efficiency</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tickets.map(ticket => {
                    const efficiency = calculateEfficiency(ticket);
                    const bookMinutes = ticket.labor_approved_minutes ?? 0;
                    const actualMinutes = ticket.labor_actual_minutes ?? 0;

                    return (
                      <TableRow key={ticket.id}>
                        <TableCell className="whitespace-nowrap">
                          {formatInTimeZone(new Date(ticket.completed_at), shopTimezone, 'MM/dd/yy HH:mm')}
                        </TableCell>
                        <TableCell>{ticket.customer_name || 'N/A'}</TableCell>
                        <TableCell>
                          {ticket.vehicle ? `${ticket.vehicle.year} ${ticket.vehicle.make} ${ticket.vehicle.model}` : 'N/A'}
                        </TableCell>
                        <TableCell>{ticket.technician?.name || 'N/A'}</TableCell>
                        <TableCell>
                          <div className="max-w-xs">
                            {ticket.ticket_jobs?.map((tj: any, idx: number) => (
                              <div key={idx} className="text-xs">
                                {tj.job?.name}
                                {tj.actual_minutes && (
                                  <span className="text-muted-foreground ml-1">({tj.actual_minutes}m)</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{bookMinutes}m</TableCell>
                        <TableCell className="text-right">{actualMinutes}m</TableCell>
                        <TableCell className="text-right">
                          {efficiency ? (
                            <Badge variant={efficiency >= 100 ? 'default' : 'secondary'}>
                              {efficiency}%
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">N/A</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}